import { getAdaptiveConfigurationValue } from '../AdaptiveConfigurationDto';
import { createNodes, traverseForest } from '@canvas-logic/clp-domain';
import { InvalidViewError } from './errors';
import { assertUnreachable } from '@canvas-logic/clp-utils';
export class View3dConfiguration {
    constructor(configDto) {
        var _a, _b, _c, _d;
        this.configDto = configDto;
        this.nodes = [];
        this.existingMaterials = new Set();
        this.existingViews = new Set();
        this.existingParts = new Set();
        this.views = configDto.views;
        this.parts = configDto.parts;
        this.nodes = createNodes(configDto.parts);
        this.pdfCameraPosition = configDto.pdfCameraPosition;
        this.scene = configDto.scene;
        this.renderer = configDto.renderer;
        this.camera = this.createCameraConfig(configDto.camera);
        this.hdr = getAdaptiveConfigurationValue((_a = configDto.hdr) !== null && _a !== void 0 ? _a : '');
        this.clearColor = configDto.clearColor || '#eeee';
        this.assets = {
            materials: (_c = (_b = configDto.materials) === null || _b === void 0 ? void 0 : _b.map(createMaterial)) !== null && _c !== void 0 ? _c : [],
            models: [],
            textures: (_d = configDto.textures) !== null && _d !== void 0 ? _d : [],
        };
        this.validateInvariants();
    }
    validateInvariants() {
        this.registerViews();
        this.registerParts();
        this.registerMaterials();
        this.validateViewsInvariants();
        this.validateHierarchyInvariants();
        this.validateMaterialsInvariants();
        this.validatePartsInvariants();
    }
    validateHierarchyInvariants() {
        const visitedParts = new Set();
        const parts = new Set(this.parts.map((part) => part.partKey));
        traverseForest(this.nodes, (next) => {
            visitedParts.add(next.partKey);
        });
        const invalidParts = [];
        for (const part of parts) {
            if (!visitedParts.has(part)) {
                invalidParts.push(part);
            }
        }
        if (invalidParts.length > 0) {
            invalidParts.sort();
            throw new Error(`Invalid hierarchy for parts "${invalidParts.join(', ')}"`);
        }
    }
    validateViewsInvariants() {
        const visitedViews = new Set();
        for (const view of this.views) {
            if (view.key === '') {
                throw new InvalidViewError(`Invalid views. Empty key for view`, view);
            }
            if (view.url === '') {
                throw new InvalidViewError(`Invalid views. Empty url for view`, view);
            }
            if (visitedViews.has(view.key)) {
                throw new InvalidViewError(`Invalid views. Duplicated key "${view.key}"`, view);
            }
            visitedViews.add(view.key);
        }
    }
    createCameraConfig(camera) {
        var _a, _b, _c, _d, _e, _f, _g;
        return {
            minDistance: (_a = camera.minDistance) !== null && _a !== void 0 ? _a : View3dConfiguration.defaultCameraMinDistance,
            maxDistance: (_b = camera.maxDistance) !== null && _b !== void 0 ? _b : View3dConfiguration.defaultCameraMaxDistance,
            maxPolarAngle: (_c = camera.maxPolarAngle) !== null && _c !== void 0 ? _c : View3dConfiguration.defaultCameraMaxPolarAngle,
            minAzimuthAngle: (_d = camera.minAzimuthAngle) !== null && _d !== void 0 ? _d : View3dConfiguration.defaultMinAzimuthAngle,
            maxAzimuthAngle: (_e = camera.maxAzimuthAngle) !== null && _e !== void 0 ? _e : View3dConfiguration.defaultMaxAzimuthAngle,
            farFactor: (_f = camera.farFactor) !== null && _f !== void 0 ? _f : 2,
            position: getAdaptiveConfigurationValue(camera.position),
            lookAt: camera.lookAt ? getAdaptiveConfigurationValue(camera.lookAt) : { x: 0, y: 0, z: 0 },
            autoZoomDisable: (_g = camera.autoZoomDisable) !== null && _g !== void 0 ? _g : false,
        };
    }
    registerViews() {
        for (const view of this.views) {
            this.existingViews.add(view.key);
        }
    }
    registerParts() {
        for (const part of this.parts) {
            this.existingParts.add(part.partKey);
        }
    }
    validateMaterialsInvariants() {
        var _a;
        const visitedMaterials = new Set();
        for (const material of (_a = this.configDto.materials) !== null && _a !== void 0 ? _a : []) {
            if (material.id === '') {
                throw new Error(`Invalid materials. Empty id for material "${JSON.stringify(material)}"`);
            }
            if ('file' in material) {
                if (material.file === '') {
                    throw new Error(`Invalid materials. Empty file for material "${material.id}"`);
                }
                if (material.name === '') {
                    throw new Error(`Invalid materials. Empty name for material "${material.id}"`);
                }
            }
            if (visitedMaterials.has(material.id)) {
                throw new Error(`Invalid materials. Duplicated id "${material.id}"`);
            }
            visitedMaterials.add(material.id);
        }
    }
    validatePartsInvariants() {
        for (const part of this.parts) {
            if (part.partKey === '') {
                throw new Error(`Invalid parts. Empty id for "${part}"`);
            }
            const visitedMeshes = new Set();
            for (const material of part.materials) {
                if (visitedMeshes.has(material.mesh)) {
                    throw new Error(`Invalid parts. Materials of part "${part.partKey}" reference the same mesh "${material.mesh}"`);
                }
                visitedMeshes.add(material.mesh);
            }
        }
    }
    registerMaterials() {
        var _a;
        for (const material of (_a = this.configDto.materials) !== null && _a !== void 0 ? _a : []) {
            this.existingMaterials.add(material.id);
        }
    }
}
View3dConfiguration.defaultCameraMinDistance = 1.3;
View3dConfiguration.defaultCameraMaxDistance = 10;
View3dConfiguration.defaultCameraMaxPolarAngle = 90;
View3dConfiguration.defaultMinAzimuthAngle = Infinity;
View3dConfiguration.defaultMaxAzimuthAngle = Infinity;
function createMaterial(config) {
    const type = config.type;
    switch (type) {
        case 'shaderMaterial': {
            const shaderMaterial = {
                id: config.id,
                type: 'shaderMaterialAsset',
                fragmentShader: config.shader.fragment,
                vertexShader: config.shader.vertex,
                uniforms: config.shader.uniforms || {},
                transparent: config.shader.transparent || false,
            };
            return shaderMaterial;
        }
        case 'meshMaterial': {
            const meshMaterial = {
                id: config.id,
                type: 'meshMaterialAsset',
                fileName: config.file,
                meshName: config.name,
            };
            return meshMaterial;
        }
        case 'standardMaterial': {
            const material = {
                id: config.id,
                type: 'standardMaterialAsset',
                metalness: 0,
                roughness: 0,
                envMapIntensity: 1,
                color: '#000',
            };
            return material;
        }
        case 'physicalMaterial': {
            const material = {
                id: config.id,
                type: 'physicalMaterialAsset',
                metalness: 0,
                roughness: 0,
                color: '#000',
                clearcoat: 0,
                clearcoatRoughness: 0,
            };
            return material;
        }
        default:
            assertUnreachable(type, `Unsupported material type "${type}" for ${JSON.stringify(config)}`);
    }
}
//# sourceMappingURL=View3dConfiguration.js.map